import React, {useState} from 'react';
import {connect} from 'react-redux';
import {Button, DescriptiveText, InlineSpinner, LoadingSpinner, Tile} from '../../../shared';
import PrefCollection from './PrefCollection';
import debug from 'debug';
import PropTypes from 'prop-types';
import axiosInstance from '../../utils/axios';
import {parseErrorMessage} from '../../utils/utils';
import {_setNotification} from '../../reducers/headerNotification';
import Column from '../Column';
import BaseModal from '../modals/types/Base';
import {_removeModal} from '../../reducers/modals';
import history from '../../../history';

const log = debug('circleblack:preferences:panel');

const PrefPanel = React.forwardRef((props, ref) => {
  const {setNotification, session, content, removeModal, width} = props;
  log('rendering');
  const [status, setStatus] = useState(<>&nbsp;</>);
  const [panel, setPanel] = useState();
  const [saving, setSaving] = useState({queue: [], status: false});
  const {id, panelName, cat, target, preferenceButtons, preferenceCheckboxes, collectionName} = content;

  React.useEffect(() => {
    setStatus(<InlineSpinner/>);
    axiosInstance.get(
      `/preferences/${cat.name}/${cat.id}/panels/${panelName}`,
      {params: target ? {target: target.name, targetID: target.id} : {}}
    )
      .then(res => {
        res.data.cores = {};
        res.data.coresID = {};
        res.data.collections.forEach(collection => {
          collection.groups.forEach(group => {
            group.cores.forEach(core => {
              res.data.coresID[core.id] = core;
              res.data.cores[core.title] = core;
            });
          });
        });
        res.data.cat = cat;
        res.data.target = target;
        log('Preferences', res.data);
        setPanel(res.data);
        setStatus(<>&nbsp;</>);
      })
      .catch(err => {
        setNotification({message: parseErrorMessage(err)});
      });
  }, [session]); // ETODO this is only needed for when entitlements get changed and it's changing too often currently.

  const processSave = () => {
    if (saving.status || !saving.queue.length) {
      log('NO Process Saving', JSON.stringify(saving));
      return;
    }
    const {title, value} = saving.queue.shift();
    log('YES Process Saving', JSON.stringify(saving), title, value);
    setStatus(<InlineSpinner/>);
    setSaving({status: true, queue: [...saving.queue]});

    const body = {[title]: value};

    if (target) {
      body.target = target.name;
      body.targetID = target.id;
    }

    return axiosInstance.put('/preferences/' + cat.name + '/' + cat.id, body)
      .then(() => {
        setStatus(<>&nbsp;</>);
        setSaving(current => ({saving: false, queue: current.queue}));
      })
      .catch(err => setStatus(<DescriptiveText>`Failed: ${parseErrorMessage(err)}`</DescriptiveText>));
  };

  React.useEffect(() => {
    processSave();
  }, [saving]);

  const collection = React.useMemo(
    () => collectionName && panel && panel.collections.find(c => c.title === collectionName),
    [collectionName, panel]
  );

  const collectionBack = () => {
    // ETODO Improve this
    switch (panel.title) {
      case 'wealthAlertsPreferences':
        return history.push('/compliance/wealthalerts');
      default:
        return history.goBack();
    }
  };

  const updateCore = (title, value, invalid) => {
    const core = panel.cores[title];
    log('updateCore', JSON.stringify(core), value, invalid);
    if (core.val instanceof Object) {
      core.val.value = value;
    } else {
      core.val = value;
    }
    setPanel(current => ({...current, cores: {...current.cores, [title]: {...core}}}));
    if (!invalid) {
      setStatus(<InlineSpinner/>);
      setSaving(current => ({status: current.status, queue: [...current.queue.concat([{title, value}])]}));
    }
  };

  if (id) {
    if (!panel) {
      return <BaseModal onClose={() => removeModal(content)} title={'Loading'} size={'md'}>
        <LoadingSpinner/>
      </BaseModal>;
    }
    if (collectionName) {
      return <BaseModal onClose={() => removeModal(content)} title={collection.displayName} size={'md'}>
        <div>
          {status}
        </div>
        <PrefCollection key={`collection-${collection.title}`}
                        collection={collection}
                        skipTitle={true}
                        panel={panel}
                        preferenceButtons={preferenceButtons}
                        preferenceCheckboxes={preferenceCheckboxes}
                        updateCore={updateCore}/>
      </BaseModal>;
    }
    return <BaseModal onClose={() => removeModal(content)} title={panel.displayName} size={'md'}>
      <div>
        {status}
      </div>
      {panel.description && <DescriptiveText alignment={'left'}>
        <span dangerouslySetInnerHTML={{__html: panel.description}}/>
      </DescriptiveText>}
      <Column>
        {panel.collections.map(c => <PrefCollection key={`collection-${c.title}`}
                                                    collection={c}
                                                    panel={panel}
                                                    preferenceButtons={preferenceButtons}
                                                    preferenceCheckboxes={preferenceCheckboxes}
                                                    updateCore={updateCore}/>)}
      </Column>
    </BaseModal>;
  }
  // TODO Preference panel styling
  if (!panel || (collectionName && !collection)) {
    return <LoadingSpinner/>;
  }

  if (collectionName) {
    return <Tile ref={ref} {...props} key={collection.displayName} title={collection.displayName} width={width} layout={{grow: 1}}>
      <div>
        {status}
      </div>
      <Button importance={'secondary'}
              size={'md'}
              width={'fit-content'}
              label={`Back to ${panel.displayName}`}
              action={collectionBack}/>
      <PrefCollection key={`collection-${collection.title}`}
                      collection={collection}
                      skipTitle={true}
                      panel={panel}
                      preferenceButtons={preferenceButtons}
                      preferenceCheckboxes={preferenceCheckboxes}
                      updateCore={updateCore}/>
    </Tile>;
  }
  return <Tile ref={ref} {...props} key={panel.displayName} title={panel.displayName} width={width} layout={{grow: 1}}>
    <div>
      {status}
    </div>
    {panel.description && <DescriptiveText alignment={'left'}>
      <span dangerouslySetInnerHTML={{__html: panel.description}}/>
    </DescriptiveText>}
    <Column>
      {panel.collections.map(c => <PrefCollection key={`collection-${c.title}`}
                                                  collection={c}
                                                  panel={panel}
                                                  preferenceButtons={preferenceButtons}
                                                  preferenceCheckboxes={preferenceCheckboxes}
                                                  updateCore={updateCore}/>)}
    </Column>
  </Tile>;
});

const mapDispatchToProps = dispatch => ({
  setNotification: note => dispatch(_setNotification(note)),
  removeModal: modal => dispatch(_removeModal(modal)),
});

const mapStateToProps = state => ({
  session: state.session,
});

PrefPanel.displayName = 'PrefPanel';

export default connect(mapStateToProps, mapDispatchToProps)(PrefPanel);

PrefPanel.propTypes = {
  content: PropTypes.shape({
    /**
     * If passed in opens as a modal, otherwise a tile
     */
    id: PropTypes.string,
    /**
     * Name of panel to be loaded
     */
    panelName: PropTypes.string.isRequired,
    /**
     * Name of collection in panel to be loaded, if passed the panel is not displayed, only the 1 collection.
     */
    collectionName: PropTypes.string,
    /**
     * Category name and id to be loaded
     */
    cat: PropTypes.shape({id: PropTypes.number.isRequired, name: PropTypes.number.isRequired}).isRequired,
    /**
     * Optional target for the category such as setting preferences for an assistant for a specific advisor
     */
    target: PropTypes.shape({id: PropTypes.number.isRequired, name: PropTypes.number.isRequired}),
    /**
     * Pass-through to PrefCore
     */
    preferenceButtons: PropTypes.object,
    /**
     * Pass-through to PrefCore
     */
    preferenceCheckboxes: PropTypes.object,
  }),
  setNotification: PropTypes.func,
  removeModal: PropTypes.func,
  session: PropTypes.object.isRequired,
  /**
   * Tile width of PrefPanel
   */
  width: PropTypes.string,
};

PrefPanel.defaultProps = {
  width: '100%',
};
